/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetWidgetRequest,
  GetWidgetSecurity,
} from "../models/operations/getwidget.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildWidgetQuery,
  prefetchWidget,
  queryKeyWidget,
  WidgetQueryData,
} from "./widget.core.js";
export {
  buildWidgetQuery,
  prefetchWidget,
  queryKeyWidget,
  type WidgetQueryData,
};

export type WidgetQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getWidget widgets
 *
 * @remarks
 * Get one widget by id, validated as it is read.
 */
export function useWidget(
  request: GetWidgetRequest,
  security?: GetWidgetSecurity | undefined,
  options?: QueryHookOptions<WidgetQueryData, WidgetQueryError>,
): UseQueryResult<WidgetQueryData, WidgetQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildWidgetQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getWidget widgets
 *
 * @remarks
 * Get one widget by id, validated as it is read.
 */
export function useWidgetSuspense(
  request: GetWidgetRequest,
  security?: GetWidgetSecurity | undefined,
  options?: SuspenseQueryHookOptions<WidgetQueryData, WidgetQueryError>,
): UseSuspenseQueryResult<WidgetQueryData, WidgetQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildWidgetQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setWidgetData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: WidgetQueryData,
): WidgetQueryData | undefined {
  const key = queryKeyWidget(...queryKeyBase);

  return client.setQueryData<WidgetQueryData>(key, data);
}

export function invalidateWidget(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "widgets", "get", ...queryKeyBase],
  });
}

export function invalidateAllWidget(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "widgets", "get"],
  });
}
